import { useCallback, useEffect, useRef, useState } from 'react'

import { DriverNode, useAllDriversQuery } from '@/apollo/graphql'
import { getFetchMoreStatus } from '@/utils'
import { IPageInfo } from '@/types'

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = (customerId?: number) => {
  const prevKeyword = useRef<string>('')
  const [search, setSearch] = useState<string>('')
  const [drivers, setDrivers] = useState<Partial<DriverNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading, networkStatus } = useAllDriversQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      nameIncludes: search,
      customer: customerId,
    },
    onCompleted: res => {
      if (res.allDrivers?.edges) {
        const _drivers: Partial<DriverNode>[] = []
        res.allDrivers.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) _drivers.push(e.node)
        })

        setDrivers(prev => [...prev, ..._drivers])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allDrivers?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: drivers.length }))
  }, [drivers?.length])

  const onSearch = useCallback((text: string) => {
    if (prevKeyword.current !== text) {
      setDrivers([])
      prevKeyword.current = text
    }
    setSearch(text)
    setPageInfo(initialPageInfo)
  }, [])

  return {
    search,
    loading,
    pageInfo,
    onSearch,
    onFetchMore,
    data: drivers,
  }
}
